import {
  AI_WINDOW_DAYS,
  type AdminAiUserTaskSpend,
  type AdminUserAiSpend,
} from '@steado/admin-contract/ai-ops';
import { useQuery } from '@tanstack/react-query';

import { Panel, StatCards, TabSkeleton } from '@/components/panel/panels';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { ScopePills } from '@/components/table/ScopePills';
import { TextCell } from '@/components/table/cells';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { formatTokens, formatUsd } from '@/lib/format-spend';
import { AI_SPEND_WINDOW_OPTIONS } from '@/screens/ai-spend/ai-spend-search';
import { planRunTone } from '@/screens/plan-ops/plan-run-tone';

import { TabError } from './TabError';
import { userAiSpendQueryOptions } from './user-detail-query';

const COLUMNS: readonly TableColumn[] = [
  { key: 'task', label: 'Task', width: '1.3fr' },
  { key: 'calls', label: 'Calls', width: '0.6fr', align: 'right' },
  { key: 'cost', label: 'Cost', width: '0.7fr', align: 'right' },
  { key: 'share', label: 'Share', width: '0.9fr', align: 'right' },
];

/**
 * The chart ramp, so a task keeps one colour down the column and across the
 * bar beside it. Position in the list, not a hash of the name: the list is
 * sorted by cost, so the ramp reads as an order rather than as a category.
 */
const TASK_INKS = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4', 'bg-chart-5'] as const;

function humanise(task: AdminAiUserTaskSpend['task']): string {
  const words = task.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function percent(share: number | null): string {
  return share === null ? '—' : `${String(Math.round(share * 1000) / 10)}%`;
}

export function AiCostTab({
  userId,
  days = AI_WINDOW_DAYS,
  onDaysChange,
}: {
  readonly userId: string;
  /** The window, from the URL; Spend by user carries its own over. */
  readonly days?: number | undefined;
  readonly onDaysChange: (days: number) => void;
}) {
  const { api } = useAuth();
  const query = useQuery(userAiSpendQueryOptions(api, userId, days));

  const windowPills = (
    <ScopePills
      label="Window"
      scopes={AI_SPEND_WINDOW_OPTIONS.map((option) => ({ id: String(option), label: `${String(option)}d` }))}
      value={String(days)}
      onChange={(next) => {
        onDaysChange(Number(next));
      }}
    />
  );

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-3">
        {windowPills}
        <TabSkeleton rows={4} />
      </div>
    );
  }
  if (query.isError) {
    return <TabError message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const spend: AdminUserAiSpend = query.data;

  const rows: readonly TableRow[] = spend.tasks.map((task, index) => {
    const ink = TASK_INKS[index % TASK_INKS.length] ?? 'bg-chart-1';

    return {
      id: task.task,
      cells: [
        <span key="task" className="flex min-w-0 items-center gap-[7px]">
          <span aria-hidden="true" className={cn('inline-block size-[7px] shrink-0 rounded-[2px]', ink)} />
          <span className="truncate text-[13px] text-foreground">{humanise(task.task)}</span>
        </span>,
        <TextCell key="calls" value={task.calls.toLocaleString('en-IN')} mono align="right" />,
        <TextCell key="cost" value={formatUsd(task.costUsd, task.costIsPartial)} mono align="right" />,
        <span key="share" className="flex items-center justify-end gap-[7px]">
          {/*
            The bar is the column's point: four costs in dollars are four
            numbers to compare by hand, and the same four as lengths are read
            at a glance. The figure stays beside it because a bar cannot say
            "0.4%".
          */}
          <span aria-hidden="true" className="h-[5px] w-11 overflow-hidden rounded-[3px] bg-secondary">
            <span
              className={cn('block h-full rounded-[3px]', ink)}
              style={{ width: `${String(Math.round((task.share ?? 0) * 100))}%` }}
            />
          </span>
          <span className="w-[34px] text-right font-mono text-xs text-muted-foreground">
            {percent(task.share)}
          </span>
        </span>,
      ],
    };
  });

  return (
    <div className="flex flex-col gap-3">
      {windowPills}
      <StatCards
        stats={[
          {
            label: `Model spend, ${String(spend.windowDays)} days`,
            value: formatUsd(spend.totals.costUsd, spend.totals.costIsPartial),
            note: spend.totals.costIsPartial
              ? 'A floor: a call of theirs succeeded or timed out without reporting a cost.'
              : 'OpenRouter’s own figure, per call.',
          },
          {
            label: 'Share of the bill',
            value: percent(spend.shareOfWindow),
            note: 'Of everything the window cost, attributed or not.',
          },
          {
            label: 'Calls',
            value: spend.totals.calls.toLocaleString('en-IN'),
            note: `${formatTokens(spend.totals.inputTokens)} in, ${formatTokens(spend.totals.outputTokens)} out`,
          },
          {
            label: 'Plan generations',
            // The count, not the list's length: the list stops at the newest dozen.
            value: spend.planRunCount.toLocaleString('en-IN'),
            note:
              spend.planRunCount > spend.planRuns.length
                ? `Started in the window, retries included. The newest ${String(spend.planRuns.length)} are listed.`
                : 'Started in the window, retries included.',
          },
        ]}
      />

      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.15fr_1fr]">
        <DataTable
          caption="What this person spent it on"
          title="What this person spent it on"
          count={`last ${String(spend.windowDays)} days`}
          columns={COLUMNS}
          rows={rows}
          isLoading={false}
          emptyTitle="No model time in this window"
          emptyBody="Nothing this person did in this window reached a model. That is normal for an account that has not opened the coach or generated a plan."
        />

        <Panel title="Cost per plan run" description="Newest first." bleed>
          {spend.planRuns.length === 0 ? (
            <p className="border-t border-border px-[18px] py-6 text-[13px] text-muted-foreground">
              No plan has been generated for this person in the window.
            </p>
          ) : (
            <ul>
              {spend.planRuns.map((run) => (
                <li
                  key={run.runId}
                  className="flex items-center gap-3 border-t border-border px-[18px] py-2.5"
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-[13px] font-medium text-foreground">
                      {run.weekStartDate === '' ? 'No week' : `Week of ${formatDate(run.weekStartDate)}`}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {run.calls === 1 ? '1 model call' : `${String(run.calls)} model calls`}
                    </span>
                  </span>
                  <span className="ml-auto flex shrink-0 flex-col items-end gap-0.5">
                    <span className="font-mono text-[13px] font-medium text-foreground">
                      {formatUsd(run.costUsd, run.costIsPartial)}
                    </span>
                    <Badge tone={planRunTone(run.status)} size="sm">
                      {run.status.replace(/_/g, ' ')}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
